import React from 'react'
import {ProductTileCms} from '@/components/product-tile-cms'
import {useFetchBatchedSfccProducts} from 'hec/hooks/product/useFetchBatchedProductData'
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselPrevious,
    CarouselNext,
    ContentButtonNavigation,
    CarouselSlidesVisibleTypeMobile,
    CarouselSlidesVisibleTypeDesktop
} from '@/components/ui/carousel'
import {Heading} from '@/components/ui/heading'
import useMediaQuery from '@/hooks/useMediaQuery'
import {ProductDataType} from '@/types/product'
import {useCioRecommendations, useFetchCioProductCategory} from '@/hooks/constructor-io'
import {useMemoizedProductData, ProductLoadingReturn} from '@/hooks/product/useMemoizedProductData'

interface ProductRecommendationsProps {
    attributes: Record<string, string>
    podId: string
    addedToCartPodId: string
    slidesVisible: {
        onDesktop: CarouselSlidesVisibleTypeDesktop
        onMobile: CarouselSlidesVisibleTypeMobile
    }
    isAddToCart: boolean
    productIds: string[]
    groupId: string
    isCategory: boolean
    categoryId: string
}

export const ProductRecommendations: React.FC<ProductRecommendationsProps> = ({
    attributes,
    podId,
    addedToCartPodId,
    slidesVisible,
    isAddToCart = false,
    productIds = [],
    groupId,
    isCategory,
    categoryId
}) => {
    const isPodIdSet = typeof podId === 'string' && podId.length > 0
    const isCategoryIdSet = typeof categoryId === 'string' && categoryId.length > 0
    const shouldQueryRecommendations = !isCategory && isPodIdSet
    const shouldQueryCategory = isCategory && isCategoryIdSet

    const {data: cioRecommendationsData, error: cioRecommendationsError} = useCioRecommendations(
        podId,
        productIds,
        groupId,
        shouldQueryRecommendations
    )

    const {data: cioCategoryData, error: cioCategoryError} = useFetchCioProductCategory(
        categoryId,
        10,
        shouldQueryCategory
    )

    const cioErrors = cioRecommendationsError || cioCategoryError
    const cioProductResults =
        cioRecommendationsData?.response?.results || cioCategoryData?.response?.results || []
    const productIdsFromCio = cioProductResults?.map((item: any) => item?.data?.id) || []
    const resultCount =
        cioRecommendationsData?.response?.results?.length ||
        cioCategoryData?.response?.results?.length ||
        0
    const cioDisplayName =
        cioRecommendationsData?.response?.pod?.display_name ||
        (cioCategoryData?.response?.groups &&
            (cioCategoryData.response.groups as Array<{display_name?: string}>)?.find(() => true)
                ?.display_name)
    const {sfccProducts, isSfccProductsLoading} = useFetchBatchedSfccProducts(productIdsFromCio)
    const fetchedProductData = useMemoizedProductData({
        resultCount,
        sfccProducts: sfccProducts,
        productResults: cioProductResults,
        isDataLoading: isSfccProductsLoading
    })
    const isMobile = useMediaQuery('sm')

    if (cioErrors) {
        const message = isCategory
            ? `category for product category ID: ${categoryId}`
            : `recommendations for pod ID: ${podId}`

        console.error(
            `There was an issue fetching product ${message}. Error: ${JSON.stringify(cioErrors)}`
        )
    }

    return (
        <div {...attributes}>
            {cioProductResults?.length > 0 && (
                <>
                    {isMobile && (
                        <Heading
                            tag="h4"
                            variant="h4"
                            className="text-emphasis bg-gray-01 text-center w-full m-auto pt-[1.5rem] pb-[1.5rem] mt-[1rem]"
                            data-testid="productRecommendations-Heading"
                        >
                            {cioDisplayName}
                        </Heading>
                    )}

                    {!isMobile && (
                        <Heading
                            tag="h4"
                            variant="h4"
                            className="text-emphasis bg-gray-01 rounded-md text-center w-full m-auto pt-[1.5rem] pb-[1.5rem] mb-[2.5rem]"
                            data-testid="productRecommendations-Heading"
                        >
                            {cioDisplayName}
                        </Heading>
                    )}

                    <Carousel
                        opts={{loop: true, slidesToScroll: 2, align: 'start'}}
                        data-testid="productRecommendations-Carousel"
                    >
                        <CarouselPrevious
                            overlapContent={false}
                            iconAttributes={{className: 'h-6 w-6'}}
                            data-testid="productRecommendations-CarouselPrevious"
                        />
                        <CarouselContent
                            className="mx-auto"
                            data-testid="productRecommendations-CarouselContent"
                        >
                            {fetchedProductData?.map(
                                (item: ProductDataType | ProductLoadingReturn, i: number) => (
                                    <CarouselItem
                                        key={`product-${item.id}-${i}`}
                                        slidesVisible={slidesVisible?.onDesktop || '4'}
                                        slidePadding={'1'}
                                        slidesVisibleMobile={slidesVisible?.onMobile || '2'}
                                        slidePaddingMobile={'3'}
                                        data-testid="productRecommendations-CarouselItem"
                                    >
                                        <ProductTileCms
                                            isProductLoading={item.skeleton}
                                            product={item}
                                            isAddToCart={isAddToCart}
                                            addedToCartPodId={addedToCartPodId}
                                            data-testid="productRecommendations-ProductTileCms"
                                        />
                                    </CarouselItem>
                                )
                            )}
                        </CarouselContent>
                        <CarouselNext
                            overlapContent={false}
                            iconAttributes={{className: 'h-6 w-6'}}
                            data-testid="productRecommendations-CarouselNext"
                        />
                        <ContentButtonNavigation
                            buttonType={'flatButton'}
                            data-testid="productRecommendations-ContentButtonNavigation"
                        />
                    </Carousel>
                </>
            )}
        </div>
    )
}
